import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { Breadcrumbs } from '../../../smartLinks/smlBreadcrumbs/types';
import { SMLBreadcrumb } from '../../../smartLinks/smlBreadcrumbs/SMLBreadcrumb';
import { PageArtistPill } from '../../../accountSelector/PageArtistPill';
import { useCurrentNavScreen } from '../../navigation/hooks/useCurrentNavScreen';
import { useMobilePageTitle } from '../../navigation/hooks/useMobilePageTitle';
import { mobileTopBarTitleOf } from '../../navigation/mobile/mobileTopBarTitle';
import styles from './SMLPageHead.module.scss';
import { usePageTabsOnTop } from './usePageTabsOnTop';
import { withBrandWord } from '../primitives/BrandWord';

interface SMLPageHeadProps {
  breadcrumbs: Breadcrumbs;
  title: string;
  /** A sentence under the title. Settings passes a node that keeps one height for every tab. */
  subtitle?: React.ReactNode;
  /**
   * A richer line in the subtitle's place — pills, counts, a countdown. A
   * screen that has facts to state uses this; one with a sentence uses
   * `subtitle`.
   */
  meta?: React.ReactNode;
  /** An avatar or a cover left of the title (the artist Home header). */
  leading?: React.ReactNode;
  /** The back link of a page under a menu item, between the crumbs and the title (`SMLBackLink`). */
  back?: React.ReactNode;
  /**
   * The page tabs (Insights, Posts, Pitch, Charts). On a phone the head shows them first, under the
   * top bar, and drops the subtitle (design navigation-v2 g 7c). On a wider screen the head shows
   * nothing of them: the page body shows them in `PageTabsInBody`.
   */
  tabs?: React.ReactNode;
}

const SMLPageHeadComponent: React.FC<React.PropsWithChildren<SMLPageHeadProps>> = ({
  breadcrumbs,
  title,
  subtitle,
  meta,
  leading,
  back,
  tabs,
  children,
}) => {
  // design: navigation-v2 7c, 7f. On a phone the top bar carries the title, so the page shows it
  // once: the head drops its crumbs and its title there. A page under a menu item gives the top
  // bar its title. Home keeps its own title: its top bar shows the logo.
  const { screen } = useCurrentNavScreen();
  const topBarTitle = mobileTopBarTitleOf(screen);
  useMobilePageTitle(topBarTitle === 'deep' ? title : undefined);
  const titleInTopBar = topBarTitle !== 'wordmark';
  const tabsOnTop = usePageTabsOnTop() && tabs !== undefined && tabs !== null;

  return (
    <div
      className={clsx(
        styles.pageHead,
        titleInTopBar && styles.titleInTopBar,
        topBarTitle === 'deep' && styles.backInTopBar,
        tabsOnTop && styles.tabsFirst
      )}
    >
      {/* design: navigation-v2 1e. The breadcrumb row spans the page; the artist pill sits at its right. */}
      <div className={styles.crumbRow}>
        <div className={styles.eyebrow}>
          {breadcrumbs.map((breadcrumb, index) => (
            <SMLBreadcrumb key={breadcrumb.name} breadcrumb={breadcrumb} isLast={index === breadcrumbs.length - 1} />
          ))}
        </div>
        <PageArtistPill />
      </div>
      {tabsOnTop ? <div className={styles.topTabs}>{tabs}</div> : null}
      <div className={styles.headRow}>
        <div className={styles.lead}>
          {leading}
          <div className={styles.headings}>
            {back !== undefined ? <div className={styles.back}>{back}</div> : null}
            <h1 className={styles.title} title={title}>
              {withBrandWord(title)}
            </h1>
            {subtitle !== undefined && !tabsOnTop ? <p className={styles.subtitle}>{withBrandWord(subtitle)}</p> : null}
            {meta}
          </div>
        </div>
        {children !== undefined ? <div className={styles.actions}>{children}</div> : null}
      </div>
    </div>
  );
};

export const SMLPageHead = TypedMemo(SMLPageHeadComponent);
